import { FilterOptionInterface, FilterInterface } from '@teko/types/ecommerce';
import { useScreen } from 'contexts';
import { trackManually } from '@teko/tracking';
import { useRouter } from 'next/router';
import { ParsedUrlQueryInput } from 'querystring';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { logger } from '@teko/log';
import { ObjectMap, OrderBy } from 'types';
import { t } from 'i18n';
import { omitQuery } from 'helpers';

type OptionDictionary = Record<string, FilterOptionInterface>;

interface SortOptionInterface {
  optionCode: string | null;
  optionName: string;
  filterCode: string | null;
  filterName: string;
}

interface UseFilterOutput {
  displayFilters: FilterInterface[];
  clearFilterLink: string;
  activeFilterOptions: FilterOptionInterface[];
  activeSortOption: SortOptionInterface;
  optionDictionary: OptionDictionary;
  defaultSortOptions: SortOptionInterface[];
  onClickFilterOption: (
    option: FilterOptionInterface,
    options?: EventOptions,
  ) => void;
  onSortByPrice: (
    priceGte: number,
    priceLte: number,
    options?: EventOptions,
  ) => void;
  onSortOptionSelected: (
    sort: string | null,
    order: string | null,
    options?: EventOptions,
  ) => void;
  onChangePage: (pageNumber: number, options?: EventOptions) => void;
  submit: () => void;
  clearNewQuery: () => void;
}
interface EventOptions {
  autoSubmit?: boolean;
}

const getActiveOptions = (
  query: ParsedUrlQueryInput,
  optionDictionary: OptionDictionary,
): Array<FilterOptionInterface> => {
  const activatedFilters: Array<FilterOptionInterface> = [];
  Object.keys(query).forEach((key) => {
    try {
      const values = query[key];
      const filterValues =
        typeof values == 'string' ? values.split(',') : values;
      (filterValues as Array<string>).forEach((value) => {
        if (
          optionDictionary[value] &&
          key === optionDictionary[value].filterCode
        ) {
          activatedFilters.push(optionDictionary[value]);
        }
      });
    } catch (err) {
      // There is this error thrown in server log
      // TypeError: values.split is not a function
      // defend against that error
      logger.error(err);
      logger.error(`Problematic query key: ${key} value: ${query[key]}`);
    }
  });

  return activatedFilters.map((filter) => ({
    ...filter,
  }));
};

const SORT_QUERY_JOIN_CHAR = '&';

export const joinSortQuery = ({
  sort,
  order,
}: {
  sort: string;
  order: string;
}) => {
  return `${sort}${SORT_QUERY_JOIN_CHAR}${order}`;
};

export const extractSortQuery = (joinedQuery: string) => {
  return joinedQuery.split(SORT_QUERY_JOIN_CHAR);
};

const defaultSortOptions: SortOptionInterface[] = [
  {
    optionName: t('Mặc định'),
    optionCode: null,
    filterCode: null,
    filterName: t('Sắp xếp theo'),
  },
  {
    optionName: t('Phổ biến nhất'),
    optionCode: joinSortQuery({
      sort: 'SORT_BY_TOP_SALE_QUANTITY_90_DAYS',
      order: OrderBy.DESC,
    }),
    filterCode: 'sort',
    filterName: t('Sắp xếp theo'),
  },
  {
    optionName: t('Khuyến mãi tốt nhất'),
    optionCode: joinSortQuery({
      sort: 'SORT_BY_DISCOUNT_PERCENT',
      order: OrderBy.DESC,
    }),
    filterCode: 'sort',
    filterName: t('Sắp xếp theo'),
  },
  {
    optionName: t('Giá tăng dần'),
    optionCode: joinSortQuery({ sort: 'SORT_BY_PRICE', order: OrderBy.ASC }),
    filterCode: 'sort',
    filterName: t('Sắp xếp theo'),
  },
  {
    optionName: t('Giá giảm dần'),
    optionCode: joinSortQuery({ sort: 'SORT_BY_PRICE', order: OrderBy.DESC }),
    filterCode: 'sort',
    filterName: t('Sắp xếp theo'),
  },
  {
    optionName: t('Sản phẩm mới nhất'),
    optionCode: joinSortQuery({
      sort: 'SORT_BY_PUBLISH_AT',
      order: OrderBy.DESC,
    }),
    filterCode: 'sort',
    filterName: t('Sắp xếp theo'),
  },
  {
    optionName: t('Sản phẩm bán chạy nhất'),
    optionCode: joinSortQuery({
      sort: 'SORT_BY_TOP_SALE_QUANTITY_7_DAYS',
      order: OrderBy.DESC,
    }),
    filterCode: 'sort',
    filterName: t('Sắp xếp theo'),
  },
];

interface TrackingDataPayload {
  contentName: string;
  regionName: string;
  target: string;
  payload?: {
    attributeCode?: string | null;
    attributeName?: string | null;
    screenName?: string;
    url?: string;
  };
}

export const useFilter = (
  filters: FilterInterface[],
  options?: EventOptions,
): UseFilterOutput => {
  const router = useRouter();
  const { query } = router;
  const { trackingScreenName } = useScreen();
  const [newQuery, setNewQuery] = useState<ParsedUrlQueryInput>(query);
  const rootPath =
    typeof window !== 'undefined'
      ? window.location.pathname
      : router.asPath.split('?')[0];
  const clearFilterLink =
    rootPath === '/search'
      ? `/search?router=productListing${
          query.query ? `&query=${query.query}` : ''
        }`
      : `${
          typeof window !== 'undefined' ? window.location.origin : ''
        }${rootPath}`;
  // Passing all filter values array to object to search by key, avoid loop, time complexity O(1)
  const optionDictionary: OptionDictionary = useMemo(
    () =>
      filters
        .reduce((acc, cur) => {
          return [...acc, ...cur.options];
        }, [] as FilterOptionInterface[])
        .reduce((acc, cur) => {
          const optionCode = cur.optionCode;
          return { ...acc, [optionCode!]: cur };
        }, {}),
    [JSON.stringify(filters)],
  );

  const displayFilters = useMemo(
    () => filters.filter((filter) => filter.options.length > 1),
    [filters],
  );

  const activeOptions = useMemo(
    () => ({
      activeFilterOptions: getActiveOptions(newQuery, optionDictionary),
      activeSortOption:
        defaultSortOptions.find(
          (opt) =>
            opt.optionCode ===
            joinSortQuery({
              sort: newQuery.sort as string,
              order: newQuery.order as string,
            }),
        ) || defaultSortOptions[0],
    }),
    [newQuery],
  );

  useEffect(() => {
    setNewQuery(query);
  }, [JSON.stringify(query)]);

  const submit = () => {
    modifyBehaviorBaseOnOptions(newQuery, { autoSubmit: true });
  };

  const changeRouteQuery = (routeQuery: ParsedUrlQueryInput) => {
    router.push(
      {
        pathname: router.pathname,
        query: routeQuery,
      },
      {
        pathname: rootPath,
        query: omitQuery(routeQuery),
      },
      { shallow: true },
    );
  };

  const modifyBehaviorBaseOnOptions = (
    routeQuery: ObjectMap,
    overrideOptions?: EventOptions,
  ) => {
    const { autoSubmit } = { ...options, ...overrideOptions };

    setNewQuery(routeQuery);
    if (autoSubmit) {
      changeRouteQuery(routeQuery);
      const { price_gte, price_lte } = routeQuery;
      const targetUrl = new URL(window.location.origin);
      targetUrl.pathname = rootPath;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      targetUrl.search = new URLSearchParams(routeQuery as any).toString();
      trackManually(
        'applyFilter',
        'sortFilterProduct',
        trackingScreenName!,
        JSON.stringify({
          screenName: trackingScreenName,
          ...(price_gte ? { validatedPriceGte: price_gte } : {}),
          ...(price_lte ? { validatedPriceLte: price_lte } : {}),
          url: targetUrl.toString(),
        }),
      );
    }
  };

  const onClickFilterOption = useCallback(
    (value: FilterOptionInterface, options?: EventOptions) => {
      const { page, ...filterParams } = newQuery;
      let routeQuery: ParsedUrlQueryInput;
      const targetUrl = new URL(window.location.origin);
      targetUrl.pathname = rootPath;
      const trackingData: TrackingDataPayload = {
        contentName: '',
        regionName: 'sortFilterProduct',
        target: trackingScreenName!,
        payload: {},
      };
      if (value.filterCode === 'price') {
        const {
          ['price_gte']: tbr,
          ['price_lte']: tbr2,
          ...restFilterParams
        } = filterParams;
        routeQuery = restFilterParams;
      } else if (filterParams[value.filterCode]) {
        // if filterCode existed
        const filterCodeValues =
          filterParams[value.filterCode]?.toString().split(',') || [];
        const index = filterCodeValues.indexOf(value.optionCode);
        // if this option is selected before
        if (index !== -1) {
          filterCodeValues.splice(index, 1);
        } else {
          // else if we are select a new option
          filterCodeValues.push(value.optionCode);
          trackingData.contentName = value.optionName;
          trackingData.payload = {
            attributeCode: value.optionCode,
            attributeName: value.optionName,
            screenName: trackingScreenName,
          };
        }
        if (filterCodeValues.length) {
          routeQuery = {
            ...filterParams,
            [value.filterCode]: filterCodeValues.join(','),
          };
        } else {
          const { [value.filterCode]: tbr, ...restFilterParams } = filterParams;
          routeQuery = restFilterParams;
        }
      } else {
        routeQuery = {
          ...filterParams,
          [value.filterCode]: value.optionCode,
        };
        trackingData.contentName = value.optionName;
        trackingData.payload = {
          attributeCode: value.filterCode,
          attributeName: value.filterName,
          screenName: trackingScreenName,
        };
      }
      // if user select a filter then track
      if (trackingData.contentName) {
        targetUrl.search = new URLSearchParams(
          routeQuery as ObjectMap,
        ).toString();
        trackingData.payload = { url: targetUrl.toString() };
        trackManually(
          trackingData.contentName,
          trackingData.regionName,
          trackingData.target,
          JSON.stringify(trackingData.payload),
        );
      }
      modifyBehaviorBaseOnOptions(routeQuery, options);
    },
    [newQuery],
  );

  const onSortByPrice = useCallback(
    (priceGte = 0, priceLte = 0, options?: EventOptions): void => {
      const { page, price_gte, price_lte, ...filterParams } = newQuery;
      const [validPriceGte, validPriceLte] =
        priceGte < priceLte ? [priceGte, priceLte] : [priceLte, priceGte];
      const routeQuery = {
        ...filterParams,
        ...(validPriceGte && { price_gte: validPriceGte }),
        ...(validPriceLte && { price_lte: validPriceLte }),
      };
      modifyBehaviorBaseOnOptions(routeQuery, options);
      trackManually(
        'sortProductResult',
        'sortFilterProduct',
        `${priceGte},${priceLte}`,
        'pricerange',
      );
    },
    [newQuery],
  );

  const onSortOptionSelected = useCallback(
    (sort: string | null, order: string | null, options?: EventOptions) => {
      const { page, ...filterParams } = newQuery;
      let routeQuery: ParsedUrlQueryInput;
      if (
        (sort === null && order === null) ||
        (sort === query.sort && order === query.order)
      ) {
        const { sort: tbr, order: tbr2, ...restFilterParams } = filterParams;
        routeQuery = restFilterParams;
      } else {
        routeQuery = {
          ...filterParams,
          sort: sort,
          order: order,
        };
      }
      modifyBehaviorBaseOnOptions(routeQuery, options);
    },
    [newQuery],
  );

  const onChangePage = useCallback(
    (pageNumber: number, options?: EventOptions) => {
      const routeQuery = {
        ...newQuery,
        ...{ page: pageNumber },
      };
      modifyBehaviorBaseOnOptions(routeQuery, options);
    },
    [newQuery],
  );

  const clearNewQuery = () => {
    setNewQuery(query);
  };

  return {
    displayFilters,
    optionDictionary: optionDictionary,
    ...activeOptions,
    onClickFilterOption,
    onSortByPrice,
    onSortOptionSelected,
    onChangePage,
    clearFilterLink,
    submit,
    defaultSortOptions,
    clearNewQuery,
  };
};
